import useSWR from "swr";
import useSWRInfinite from "swr/infinite";
import { useDebounce } from "use-debounce";
import { instance } from "@/frontend/lib/hc";

/** One chat in a chat picker, from GET /api/summaries/v3/chat-options. */
export interface ChatOption {
  conversationId: string;
  conversationName: string;
  conversationType: "dm" | "group";
  platform: string;
  lastMessageAt: string | null;
}

interface ChatOptionsPage {
  data: ChatOption[];
  total: number;
}

export interface ChatOptionsFilter {
  type?: "dm" | "group";
  platform?: string;
  /** Any of these platforms */
  platforms?: string[];
  /** Only chats seen by this connected number, e.g. a campaign's sender */
  userPhone?: string;
}

const URL = "/api/summaries/v3/chat-options";
export const CHAT_OPTIONS_PAGE_SIZE = 50;
/** Saved IDs are named this many per request, so long lists stay under the URL size limit */
export const CHAT_OPTIONS_IDS_PER_REQUEST = 100;
const SEARCH_DEBOUNCE_MS = 300;

export function buildChatOptionsUrl(
  organizationId: string,
  filter: ChatOptionsFilter & { search?: string; ids?: string[]; limit?: number; offset?: number },
) {
  const params = new URLSearchParams({ organizationId });
  if (filter.type) params.set("type", filter.type);
  if (filter.platform) params.set("platform", filter.platform);
  if (filter.platforms?.length) params.set("platforms", filter.platforms.join(","));
  if (filter.userPhone) params.set("userPhone", filter.userPhone);
  if (filter.search) params.set("search", filter.search);
  if (filter.ids) params.set("ids", filter.ids.join(","));
  if (filter.limit) params.set("limit", String(filter.limit));
  if (filter.offset) params.set("offset", String(filter.offset));
  return `${URL}?${params.toString()}`;
}

const NO_OPTIONS: ChatOption[] = [];

const fetchPage = (url: string) => instance.get(url).json<ChatOptionsPage>();

/** Chats for a picker, a page at a time, searched on the server once typing pauses. */
export function useChatOptions(params: ChatOptionsFilter & {
  organizationId?: string;
  search?: string;
  enabled?: boolean;
}) {
  const { organizationId, enabled = true, type, platform, platforms, userPhone } = params;
  const [search] = useDebounce((params.search ?? "").trim(), SEARCH_DEBOUNCE_MS);
  const { data, error, isLoading, isValidating, size, setSize, mutate } = useSWRInfinite<ChatOptionsPage>(
    (pageIndex, previous: ChatOptionsPage | null) => {
      if (!organizationId || !enabled) return null;
      if (previous && (pageIndex * CHAT_OPTIONS_PAGE_SIZE >= previous.total)) return null;
      return buildChatOptionsUrl(organizationId, {
        type, platform, platforms, userPhone, search,
        limit: CHAT_OPTIONS_PAGE_SIZE,
        offset: pageIndex * CHAT_OPTIONS_PAGE_SIZE,
      });
    },
    fetchPage,
    { revalidateOnFocus: false, revalidateFirstPage: false },
  );
  const options = data?.flatMap((page) => page.data) ?? [];
  const total = data?.[0]?.total ?? 0;
  return {
    options,
    total,
    error,
    isLoading: isLoading || (search !== (params.search ?? "").trim() && enabled),
    isLoadingMore: isValidating && size > (data?.length ?? 0),
    hasMore: options.length < total,
    loadMore: () => setSize(size + 1),
    retry: () => mutate(),
  };
}

/** Names for saved chats, asked for in batches so a list of hundreds still fits in the URL. */
export async function fetchChatOptionsByIds(organizationId: string, ids: string[]): Promise<ChatOption[]> {
  const batches: string[][] = [];
  for (let start = 0; start < ids.length; start += CHAT_OPTIONS_IDS_PER_REQUEST) {
    batches.push(ids.slice(start, start + CHAT_OPTIONS_IDS_PER_REQUEST));
  }
  const pages = await Promise.all(batches.map((batch) => fetchPage(buildChatOptionsUrl(organizationId, { ids: batch }))));
  return pages.flatMap((page) => page.data);
}

/** Names for chats a feature already saved, so they show even when they're not on the first page. */
export function useChatOptionsByIds(organizationId: string | undefined, ids: string[]) {
  const key = organizationId && ids.length > 0 ? ["chat-options-by-ids", organizationId, ...[...ids].sort()] : null;
  // Keep the last names while a changed selection loads, so chips don't flash "Loading…"
  const { data, error, isLoading, mutate } = useSWR<ChatOption[]>(
    key,
    ([, orgId, ...sortedIds]: string[]) => fetchChatOptionsByIds(orgId, sortedIds),
    { revalidateOnFocus: false, keepPreviousData: true },
  );
  return { options: data ?? NO_OPTIONS, error, isLoading, retry: () => mutate() };
}

/** How many chats of a type (optionally by name) there are today, e.g. "12 groups match". */
export function useChatMatchCount(params: ChatOptionsFilter & {
  organizationId?: string;
  nameFilter?: string;
  enabled?: boolean;
}) {
  const { organizationId, enabled = true, type, platform, platforms, userPhone } = params;
  const nameFilter = (params.nameFilter ?? "").trim();
  const [search] = useDebounce(nameFilter, SEARCH_DEBOUNCE_MS);
  const key = organizationId && enabled
    ? buildChatOptionsUrl(organizationId, { type, platform, platforms, userPhone, search, limit: 1 })
    : null;
  const { data, isLoading } = useSWR<ChatOptionsPage>(key, fetchPage, { revalidateOnFocus: false });
  return { count: data?.total ?? 0, isCounting: Boolean(key) && (isLoading || search !== nameFilter) };
}
